import { GenericAbortSignal } from 'axios';

import { fetchGroupCodesData } from '@/services/template';
import { EXPORT_CURRENT_FPS, EXPORT_QUALITY, EXPORT_RESOLUTION } from '@/utils/const';
import request, { ErrorType } from '@/utils/request';

import { ApiResult } from '.';

/**
 * 素材包列表（带详情）
 * 公共接口，通过groupCode获取各种素材包的数据：
 * * 音色
 *    - 热门
 *    - 中文
 *    - 英文
 * * 数字人
 *    - 模板
 * * 脚本模板
 */
type FetchConfigDataByGroupCode = {
  groupCodes: string;
  pageNum: number;
  pageSize: number;
};
export const fetchConfigDataByGroupCode = async (
  data: FetchConfigDataByGroupCode,
): Promise<ApiResult<any>> => {
  return new Promise(async (resolve, reject) => {
    const res = await request('/api/rest/dlt/template/detail', {
      method: 'POST',
      data,
    });

    if (res.isError) {
      reject(res);
    } else {
      resolve(res);
    }
  });
};

export interface MusicPersonalTemplateItem {
  createTime: number;
  customType: number;
  gender: number;
  id: number;
  isPermanent: number;
  modifyTime: number;
  originUrl: string;
  previewUrl: string;
  programType: number;
  startTime: number;
  state: number;
  systemTime: number;
  title: string;
  type: number;
  duration?: number;
}

/**
 * 获取个人模板列表
 * data.type:
 *   - 1-视频模板，我的数字人
 *   - 2-音频模板, 我的音色
 */
interface FetchPersonalTemplateList {
  type: string | number;
  states?: number[];
  pageNum: number;
  pageSize: number;
}
export const fetchPersonalTemplateList = async (
  data: FetchPersonalTemplateList,
): Promise<ApiResult<any>> => {
  return new Promise(async (resolve, reject) => {
    const res = await request('/api/rest/dhc/template/personal/list', {
      method: 'POST',
      data,
    });
    if (res.isError) {
      reject(res);
    } else {
      resolve(res);
    }
  });
};

export const fetchPersonalTemplateListV2 = async (
  data: FetchPersonalTemplateList,
): Promise<ApiResult<any>> => {
  return new Promise(async (resolve, reject) => {
    const res = await request('/api/rest/dhc/template/personal/list/v2', {
      method: 'POST',
      data,
    });
    if (res.isError) {
      reject(res);
    } else {
      resolve(res);
    }
  });
};

/**
 * 生成tts
 */
export interface TTSPreviewType {
  text?: string;
  type?: number;
  srtUrl?: string;

  // 是否需要文本停顿，默认true
  needSsmlTrans: boolean;
  // 是否需要断句，默认false
  needBreak?: boolean;
  data: {
    templateId: string;
    templateType?: number;
  };
}
export const fetchTTSPreview = async (
  data: TTSPreviewType,
  signal?: GenericAbortSignal,
): Promise<ApiResult<any>> => {
  const res = await request('/api/rest/dhc/task/simple/ttsPreview', {
    method: 'POST',
    data,
    signal,
  });
  return res;
};

/**
 * 查询生成tts状态
 */
export const fetchTTSQueryResult = async (
  data: {
    businessId: string;
  },
  signal?: GenericAbortSignal,
): Promise<ApiResult<any>> => {
  const res = await request('/api/rest/dlt/cloud/make/queryResult', {
    method: 'POST',
    data,
    signal,
  });
  return res;
};

/**
 * 更新工程
 * @param data
 * @returns
 */
interface IProjectProps {
  projectName?: string;
  fileSize?: number;
  coverUrl?: string;
  downloadUrl?: string;
  desc?: string;
  extendInfo?: any;
  visible?: number;
  customType?: number;
}
interface ProjectData extends IProjectProps {
  projectId: number;
  version?: number;
}

export const projectUpdate = async (data: ProjectData): Promise<ApiResult<any>> => {
  const res = await request('/api/rest/dlt/project/update', {
    method: 'POST',
    data,
  });
  return res;
};

/**
 * 导出工程
 * @param data
 * @returns
 */
interface IClipItem {
  clipId: string; // 数字人所在comp 的 uuid
  clipTemplateCode?: string; //当前clip使用的素材code
  ttsUrl?: string; //
}

interface IExportData {
  projectId: number;
  title?: string;
  version?: number;
  projectWidth: number;
  projectHeight: number;
  resolution?: number;
  fps?: number;
  quality?: number;
  projectDuration?: number;
  clips?: Array<IClipItem>; //仅需要数字人的clip

  lipSync?: boolean; // 是否需要唇音驱动
  videoUrl?: string; // lipSync=true 且lmkUrl 与rewindUrl 为空时必须，用于数字人训练的原视频，字幕翻译，字幕&旁边翻译时为结果中noSubtitlesVideoUrl，旁白翻译时为结果中 originVideoUrl
  lmkUrl?: string; // 用于唇音驱动的点位 url
  rewindUrl?: string; // 用于唇音驱动的正倒放视频 url
  clipId?: string; // lipSync=true时必须
  voiceDataJson?: string; // lipSync=true时必须，合并音频的json 文件
}

export const projectExport = async (data: IExportData): Promise<ApiResult<any>> => {
  const res = await request('/api/rest/dlt/project/export/submit', {
    method: 'POST',
    data: {
      quality: EXPORT_QUALITY,
      fps: EXPORT_CURRENT_FPS,
      resolution: EXPORT_RESOLUTION,
      ...data,
    },
  });
  return res;
};

export const SubtitleTranslationProjectExport = async (
  data: IExportData,
): Promise<ApiResult<any>> => {
  const res = await request('/api/rest/dlt/project/translate/calibration', {
    method: 'POST',
    data: {
      quality: EXPORT_QUALITY,
      fps: EXPORT_CURRENT_FPS,
      resolution: EXPORT_RESOLUTION,
      ...data,
    },
  });
  return res;
};

/**
 * 新建工程,返回projectid
 * @param data
 * @returns
 */
export const projectCreate = async (data: IProjectProps): Promise<ApiResult<any>> => {
  return new Promise(async (resolve, reject) => {
    const res = await request('/api/rest/dlt/project/add', {
      method: 'POST',
      data,
    });

    if (res.isError) {
      reject(res);
    } else {
      resolve(res);
    }
  });
};

// 查询数字人详情
export const fetchAvatarDetail = async (data: {
  templateCodes: string;
}): Promise<ApiResult<any>> => {
  return new Promise(async (resolve, reject) => {
    const res = await request('/api/rest/dlt/template/detail/list', {
      method: 'POST',
      data,
    });
    if (res.isError) {
      reject(res);
    } else {
      resolve(res);
    }
  });
};

/**
 * 工程详情
 * @param data
 * @returns
 */

interface ITemplateItemPrj {
  downloadUrl: string;
  eventFromTemplateInfo: string;
  prjResourceType: number;
  width: number;
  height: number;
  iconFromTemplate: string;
  subTcid: string;
  tcid: string;
  templateCode: string;
  templateExtend: string;
  templateRule: string;
  titleFromTemplate: string;
  fileId: string | number;
}
interface IProjectDetailData {
  assets: Array<ITemplateItemPrj>;
  templates: Array<ITemplateItemPrj>;
  correct: boolean;
  coverUrl: string;
  downloadUrl: string;
  projectId: number;
  projectName: string;
  status: number;
  version: number;
  extendInfo: string;
  createTime: number;
}
export const projectDetail = async (
  projectId: number,
): Promise<ApiResult<IProjectDetailData>> => {
  return new Promise(async (resolve, reject) => {
    const res = await request('/api/rest/dlt/project/detail', {
      method: 'POST',
      data: { projectId },
    });
    if (res.isError) {
      reject(res);
    } else {
      resolve(res);
    }
  });
};

// 获取字体列表
export const fetchFontList = async (code = 'fontTemplate') => {
  try {
    const { success, data } = await fetchGroupCodesData(code);
    if (success && data.length) {
      const groupCodes = data.map((code: any) => code.groupCode).join(',');
      const ret = await fetchConfigDataByGroupCode({
        groupCodes,
        pageNum: 1,
        pageSize: 100,
      });
      return ret?.data || [];
    } else {
      throw Error('Get Font List Error');
    }
  } catch (error) {
    console.error(error);
    return [];
  }
};
